import { requisitarJson } from '../../../shared/api/http';

export type Fase = 'scanpixel' | 'labpixels' | 'player1' | 'versus' | 'menuresolucao';
export type Valor = string | number | boolean | null;

export interface CampoConteudo {
    entidade: string;
    id: string;
    local: string;
    campo: string;
    chave: string;
    rotulo: string;
    /**
     * `paragrafo`: texto de várias linhas, obrigatório. `inteiro_opcional`: vazio grava «sem valor».
     * `booleano` só marca (a correta); `chave` é sim/não livre (a situação ativa do Menu Resolução).
     */
    tipo: 'texto' | 'senha' | 'paragrafo' | 'texto_longo' | 'inteiro' | 'inteiro_opcional' | 'booleano' | 'chave';
    valor: Valor;
}

export type EntidadeEstrutural = 'enigma' | 'pista' | 'atividade' | 'pergunta';

/** Enigma, pista, atividade ou pergunta, com o que o painel pode fazer com ele agora. */
export interface ItemDaEstrutura {
    entidade: EntidadeEstrutural;
    id: string;
    /** O mesmo `local` dos campos: é por ele que o item encontra o seu grupo na tela. */
    local: string;
    pode_excluir: boolean;
    motivo_excluir: string | null;
    pode_incluir_filho: boolean;
    motivo_incluir_filho: string | null;
}

export interface EstruturaDaFase {
    itens: ItemDaEstrutura[];
    /** `null`: a fase não permite incluir itens pelo painel (Player 1 e Modo Versus). */
    pode_incluir: boolean | null;
    motivo_incluir: string | null;
}

export interface CamposDaFase {
    fase: Fase;
    editavel: boolean;
    turmas_com_fase_aberta: string[];
    campos: CampoConteudo[];
    estrutura?: EstruturaDaFase;
}

export interface NovaPista {
    enunciado: string;
    valor_esperado: string;
}

export interface NovoEnigma extends NovaPista {
    texto_charada: string;
}

export interface NovaPergunta {
    enunciado: string;
    pontos: number;
    /** Sempre quatro, na ordem A–D. */
    opcoes: string[];
    correta: 'A' | 'B' | 'C' | 'D';
}

export interface NovaAtividade {
    titulo: string;
    tempo_segundos: number | null;
    pergunta: NovaPergunta;
}

export interface ItemCriado {
    entidade: string;
    id: string;
    local: string;
}

export interface CampoAlterado {
    chave: string;
    id: string;
    valor: Valor;
    alterado: boolean;
}

export function listarCampos(fase: Fase): Promise<CamposDaFase> {
    return requisitarJson(`/api/v1/conteudo?fase=${fase}`);
}

export function alterarCampo(
    campo: CampoConteudo,
    valor: Valor,
    valorAnterior: Valor,
    csrfToken: string,
): Promise<CampoAlterado> {
    return requisitarJson(`/api/v1/conteudo/${campo.entidade}/${campo.id}`, {
        method: 'PATCH',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campo: campo.campo, valor, valor_anterior: valorAnterior }),
    });
}

function incluir(caminho: string, dados: unknown, csrfToken: string): Promise<ItemCriado> {
    return requisitarJson(`/api/v1/conteudo/${caminho}`, {
        method: 'POST',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dados),
    });
}

export const incluirEnigma = (dados: NovoEnigma, csrfToken: string) => incluir('enigma', dados, csrfToken);
export const incluirPista = (enigmaId: string, dados: NovaPista, csrfToken: string) =>
    incluir(`enigma/${enigmaId}/pista`, dados, csrfToken);
export const incluirAtividade = (dados: NovaAtividade, csrfToken: string) => incluir('atividade', dados, csrfToken);
export const incluirPergunta = (atividadeId: string, dados: NovaPergunta, csrfToken: string) =>
    incluir(`atividade/${atividadeId}/pergunta`, dados, csrfToken);

export function excluirItem(item: Pick<ItemDaEstrutura, 'entidade' | 'id'>, csrfToken: string): Promise<void> {
    return requisitarJson(`/api/v1/conteudo/${item.entidade}/${item.id}`, { method: 'DELETE', csrfToken });
}

/** Scan Pixel (AP12): as dicas do último enigma na ordem que o jogador vê, com o campo de cada PISTA. */
export interface UltimoEnigma {
    enigma: number | null;
    editavel: boolean;
    turmas_com_fase_aberta: string[];
    pistas: { id: string; posicao: number; campo: number; resposta: string; enunciado: string }[];
}

export function lerUltimoEnigma(): Promise<UltimoEnigma> {
    return requisitarJson('/api/v1/conteudo/scanpixel/ultimo-enigma');
}

/** Grava o campo de cada PISTA (da 1 à n), o mesmo para todas as turmas. */
export function salvarRelacaoUltimoEnigma(campos: number[], csrfToken: string): Promise<UltimoEnigma> {
    return requisitarJson('/api/v1/conteudo/scanpixel/ultimo-enigma/relacao', {
        method: 'PUT',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ campos }),
    });
}

/** Scan Pixel (AP8): a instrução do enigma abre sozinha no tablet ao entrar nele? */
export interface InstrucaoScanPixel {
    instrucao_automatica: boolean;
}

export function lerInstrucaoScanPixel(): Promise<InstrucaoScanPixel> {
    return requisitarJson('/api/v1/conteudo/scanpixel/instrucao');
}

export function definirInstrucaoScanPixel(instrucaoAutomatica: boolean, csrfToken: string): Promise<InstrucaoScanPixel> {
    return requisitarJson('/api/v1/conteudo/scanpixel/instrucao', {
        method: 'PUT',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instrucao_automatica: instrucaoAutomatica }),
    });
}

/** Por rodada do Modo Versus, o material da biblioteca que libera a resposta do chat. */
export interface MateriaisChave {
    editavel: boolean;
    turmas_com_fase_aberta: string[];
    rodadas: { atividade_id: string; local: string; material_id: string | null }[];
    materiais: { id: string; titulo: string; tem_arquivo: boolean }[];
}

export function listarMateriaisChave(): Promise<MateriaisChave> {
    return requisitarJson('/api/v1/conteudo/versus/material-chave');
}

/** `materialId: null` tira a indicação: o chat da rodada volta a ser livre. */
export function definirMaterialChave(atividadeId: string, materialId: string | null, csrfToken: string): Promise<{ material_id: string | null }> {
    return requisitarJson(`/api/v1/conteudo/versus/material-chave/${atividadeId}`, {
        method: 'PUT',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ material_id: materialId }),
    });
}

/** Player 1 (`RA13c`): a faixa da soma dos indicadores que dá cada perfil; `null` é ponta aberta. */
export interface PerfilDoPlayer1 {
    id: string;
    ordem: number;
    titulo: string;
    texto: string;
    minimo: number | null;
    maximo: number | null;
}

export interface PerfisDoPlayer1 {
    editavel: boolean;
    turmas_com_fase_aberta: string[];
    soma_minima: number;
    soma_maxima: number;
    versao: string;
    perfis: PerfilDoPlayer1[];
}

/** O que a tela manda ao salvar: sem `id`, o perfil é novo; a ordem é a da lista. */
export type PerfilEnviado = Omit<PerfilDoPlayer1, 'id' | 'ordem'> & { id: string | null };

export function obterPerfis(): Promise<PerfisDoPlayer1> {
    return requisitarJson('/api/v1/conteudo/player1/perfis');
}

/** Grava a tabela inteira; o painel recusa (422) se ela não estiver organizada. */
export function salvarPerfis(versao: string, perfis: PerfilEnviado[], csrfToken: string): Promise<PerfisDoPlayer1> {
    return requisitarJson('/api/v1/conteudo/player1/perfis', {
        method: 'PUT',
        csrfToken,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ versao, perfis }),
    });
}
